import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Typography } from '@mui/material';
import { accountDiscoveryActions, accountDiscoveryTypes } from 'app-components/flux/account-discovery';
import { rootUiActions } from 'app-components/flux/root-ui';

import { vehicleActions, vehicleThunks } from 'app-components/flux/vehicle';

import React, { useEffect, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { type Account, accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { FormProvider, vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import type {
  KbbVehicleSubmittingObject,
  ManualVehicleSubmittingObject,
  VehicleDialogStep,
  VehicleFormState,
} from 'app-components/flux/vehicle/vehicleTypes';
import { tracker } from 'app-components/utils/core';
import clsx from 'clsx';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useEffectOnce } from 'react-use';
import { makeStyles } from 'tss-react/mui';
import { DIALOG_TYPE_ASSET } from '../actions';
import { AddedStep } from './Steps/AddedStep';
import { ConditionStep } from './Steps/ConditionStep';
import { DetailsStep } from './Steps/DetailsStep';
import { ErrorStep } from './Steps/ErrorStep';
import { FetchStep } from './Steps/FetchStep';
import { ManualStep } from './Steps/ManualStep';
import { ReviewStep } from './Steps/ReviewStep';
import { BUILD_FULL_VEHICLE_NAME, formatConditionForBackend, SlideUpSnackbar } from './utils';

const useStyles = makeStyles()((theme) => ({
  vehicleDialogRoot: {
    width: '100%',
  },
  vehicleDialogContentRoot: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2.5, 7, 2.5, 7),
  },
  largeHeight: {
    minHeight: 500,
  },
  smallHeight: {
    minHeight: 300,
  },
  largeWidth: {
    maxWidth: 800,
  },
  smallWidth: {
    maxWidth: 600,
  },
  entryModeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: 'auto',
    gap: theme.spacing(0.5),
  },
  entryModeButton: {
    padding: 0,
    textTransform: 'none',
  },
}));

const InitialVehicleFormState: VehicleFormState = {
  vin: '',
  year: '',
  make: null,
  model: null,
  trim: null,
  vehicleId: null,
  miles: '',
  zipCode: '',
  inputMode: 'makeModel',
  dialogStep: 'details',
  condition: null,
  name: '',
  estimate: 0,
  userPath: 'kbb',
  linkTo: '0',
  businessId: '',
};

const VehicleDialogContinueButton = ({
  dialogStep,
  handleGoForward,
  navigateTo,
}: {
  dialogStep: VehicleDialogStep;
  handleGoForward: () => void;
  navigateTo: (patch: Partial<VehicleFormState>) => void;
}) => {
  const reduxDispatch = useAppDispatch();
  const formManager = vehicleFormHooks.useManager();

  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>('');

  const { onValueChange: setVehicleId } = vehicleFormHooks.useField('vehicleId');
  const { onValueChange: setYear } = vehicleFormHooks.useField('year');
  const { onValueChange: setMake } = vehicleFormHooks.useField('make');
  const { onValueChange: setModel } = vehicleFormHooks.useField('model');
  const { onValueChange: setTrim } = vehicleFormHooks.useField('trim');
  const { value: linkTo } = vehicleFormHooks.useField('linkTo');

  const vehicleAccountsById = useAppSelector(accountsSelectors.getVehicleAccounts);
  const vehicleAccounts = vehicleAccountsById.toList();

  const { value: accountName } = vehicleFormHooks.useField('name');

  const handleClick = async () => {
    if (isBusy) return;
    setShowTooltip(false);

    if (dialogStep === 'manual' || dialogStep === 'review') {
      const isDuplicateName = vehicleAccounts.some(
        (account: Account) => account.name?.toLowerCase() === accountName?.toLowerCase(),
      );

      if (isDuplicateName && linkTo === '0') {
        setTooltipText('Account name already exists');
        setShowTooltip(true);
        return;
      }

      if (accountsUtils.isInvalidName(accountName)) {
        setTooltipText('Invalid account name');
        setShowTooltip(true);
        return;
      }
    }

    await formManager.submit();
    if (!formManager.isFormValid()) {
      setTooltipText(dialogStep === 'condition' ? 'Selections still needed' : 'Form fields need attention');
      setShowTooltip(true);
      return;
    }

    if (dialogStep === 'details') {
      const snapshot = formManager.runSelector(({ state }) => state);

      if (snapshot.inputMode === 'vin') {
        try {
          const resolvedVehicle = await reduxDispatch(vehicleThunks.fetchKbbFullVehicleByVin(snapshot.vin)).unwrap();
          if (!resolvedVehicle.length) {
            setTooltipText('No vehicle found for this VIN');
            setShowTooltip(true);
            return;
          }

          const { yearId, makeId, makeName, modelId, modelName, trimId, trimName, modelYearId } = resolvedVehicle[0];
          setYear(String(yearId));
          setMake({ makeId: makeId, makeName: makeName });
          setModel({ modelId: modelId, modelName: modelName });
          setTrim({ trimId: trimId, trimName: trimName, modelYearId: modelYearId });

          navigateTo({ dialogStep: 'condition' });
        } catch {
          setTooltipText('Could not validate VIN');
          setShowTooltip(true);
          return;
        } finally {
          setIsBusy(false);
        }
      } else {
        navigateTo({ dialogStep: 'condition' });
      }
      return;
    }

    if (dialogStep === 'condition') {
      try {
        navigateTo({ dialogStep: 'fetch' });
        const formValues = formManager.runSelector(({ state }) => state);
        const preview = await reduxDispatch(vehicleThunks.buildVehiclePreview(formValues)).unwrap();
        const suggestedName = BUILD_FULL_VEHICLE_NAME(
          preview.year,
          preview.makeName,
          preview.modelName,
          preview.trimName,
        );
        setVehicleId(preview.vehicleId);
        navigateTo({ dialogStep: 'review', name: suggestedName });
        tracker.track(tracker.events.kbbReviewValue, { status: '200 OK', vehicleId: preview.vehicleId });
      } catch {
        navigateTo({ dialogStep: 'error' });
        tracker.track(tracker.events.kbbReviewValue, { status: '503 SERVICE UNAVAILABLE' });
      } finally {
        setIsBusy(false);
      }
      return;
    }
    handleGoForward();
  };

  return (
    <StdDialogActions
      primary={{
        children: 'Continue',
        onClick: handleClick,
        type: 'button',
        variant: 'contained',
        showTooltip,
        tooltipText: tooltipText || ' ',
        placement: 'top',
        disabled: isBusy,
        onBlur: () => setShowTooltip(false),
      }}
    />
  );
};

const VehicleDialogInner = (props: { derivedLinkTo?: string }) => {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();
  const formManager = vehicleFormHooks.useManager();
  const { derivedLinkTo } = props;

  const { onValueChange: setLinkTo } = vehicleFormHooks.useField('linkTo');
  useEffectOnce(() => setLinkTo(derivedLinkTo ?? '0'));

  const { value: userPath } = vehicleFormHooks.useField('userPath');
  const { value: dialogStep } = vehicleFormHooks.useField('dialogStep');

  const [origin, setOrigin] = useState<VehicleDialogStep | null>(null);

  const isManual = userPath === 'manual';

  const dialogFlow: VehicleDialogStep[] = isManual
    ? ['manual', 'added']
    : ['details', 'condition', 'fetch', 'review', 'added'];

  const dialogTitle = dialogStep !== 'added' ? 'Add automobile' : 'Automobile added';
  const isFinalStep = dialogStep === dialogFlow[dialogFlow.length - 1];
  const showEntryModeButton = dialogStep === 'details' || dialogStep === 'manual';
  const showDialogBackButton = dialogStep !== 'added';
  const showContinueButton = dialogStep !== 'fetch' && !isFinalStep;

  const handleDialogClose = () => {
    reduxDispatch(rootUiActions.removeDialog(DIALOG_TYPE_ASSET));
  };

  const navigateTo = (patch: Partial<Pick<VehicleFormState, 'dialogStep' | 'userPath' | 'name'>>) => {
    const snapshot = formManager.runSelector(({ state }) => state);
    const newSnapshot = { ...snapshot, ...patch };

    if (patch.dialogStep === 'review' && !derivedLinkTo) {
      setLinkTo('0');
    }

    const hasFormStateChanged =
      snapshot.dialogStep !== newSnapshot.dialogStep ||
      snapshot.userPath !== newSnapshot.userPath ||
      snapshot.name !== newSnapshot.name;

    if (hasFormStateChanged) formManager.reinit(newSnapshot);
  };

  const switchUserPath = () => {
    if (isManual) {
      navigateTo({ userPath: 'kbb', dialogStep: origin ?? 'details' });
      setOrigin(null);
    } else {
      setOrigin(dialogStep);
      navigateTo({ userPath: 'manual', dialogStep: 'manual' });
    }
  };

  const handleGoForward = () => {
    const stepIndex = dialogFlow.indexOf(dialogStep);
    const nextStep = dialogFlow[stepIndex + 1];
    if (nextStep) navigateTo({ dialogStep: nextStep });
  };

  const handleGoBack = () => {
    if (dialogStep === 'manual') {
      navigateTo({
        userPath: 'kbb',
        dialogStep: origin === 'review' ? 'review' : 'details',
      });
      return;
    }
    if (dialogStep === 'review') {
      navigateTo({ dialogStep: 'condition' });
      return;
    }
    const stepIndex = dialogFlow.indexOf(dialogStep);
    if (stepIndex > 0) {
      const previousStep = dialogFlow[stepIndex - 1];
      if (previousStep) navigateTo({ dialogStep: previousStep });
    } else {
      reduxDispatch(
        accountDiscoveryActions.doAccountDiscovery({ initiator: 'account list dashboard', mode: 'ADD-FI' }),
      );
      handleDialogClose();
    }
  };

  const renderVehicleDialogStep = () => {
    switch (dialogStep) {
      case 'details':
        return <DetailsStep />;
      case 'condition':
        return <ConditionStep />;
      case 'fetch':
        return <FetchStep />;
      case 'review':
        return <ReviewStep switchUserPath={switchUserPath} />;
      case 'added':
        return <AddedStep handleDialogClose={handleDialogClose} isManual={isManual} />;
      case 'error':
        return <ErrorStep handleDialogClose={handleDialogClose} navigateTo={navigateTo} />;
      default:
        return null;
    }
  };

  const renderVehicleDialogContent = () => {
    return (
      <>
        {dialogStep === 'manual' ? <ManualStep /> : renderVehicleDialogStep()}
        {showEntryModeButton && (
          <Box className={classes.entryModeContainer}>
            <Button className={classes.entryModeButton} endIcon={<ArrowForwardIcon />} onClick={switchUserPath}>
              {isManual ? 'Add with Kelley Blue Book instead' : 'Add manually instead'}
            </Button>
            <Typography variant='captionRegular'>
              {isManual
                ? 'Link your vehicle to its Kelley Blue Book value.'
                : `Add your vehicle’s information to Quicken without connection to Kelley Blue Book value updates.`}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <StdDialog
        title={dialogTitle}
        showBackButton={showDialogBackButton}
        onBack={handleGoBack}
        onClose={handleDialogClose}
        size='custom'
        paperProps={{
          className: clsx(classes.vehicleDialogRoot, isFinalStep ? classes.smallWidth : classes.largeWidth),
        }}
      >
        <StdDialogContent
          id='vehicle_dialog_content'
          className={clsx(classes.vehicleDialogContentRoot, isFinalStep ? classes.smallHeight : classes.largeHeight)}
        >
          {renderVehicleDialogContent()}
        </StdDialogContent>
        {showContinueButton && (
          <VehicleDialogContinueButton
            dialogStep={dialogStep}
            handleGoForward={handleGoForward}
            navigateTo={navigateTo}
          />
        )}
      </StdDialog>
      {isFinalStep && <SlideUpSnackbar message='Vehicle added.' />}
    </>
  );
};

const VehicleDialog = (props: { linkTo?: string }) => {
  const reduxDispatch = useAppDispatch();
  const [scope] = useState(() => uuidv4().toUpperCase());
  const { linkTo: derivedLinkTo } = props;

  useEffect(() => {
    reduxDispatch(
      accountDiscoveryActions.accountDiscoverySetup(accountDiscoveryTypes.mkInstitutionLoginUpserterData(), { scope }),
    );
  }, [reduxDispatch, scope]);

  return (
    <FormProvider
      input={InitialVehicleFormState}
      onSubmit={async ({ payload }) => {
        if (payload.dialogStep === 'manual') {
          const manualVehicleSubmittingObject: ManualVehicleSubmittingObject = {
            name: payload.name,
            estimate: payload.estimate,
            scope: scope,
            businessId: payload.businessId,
          };
          reduxDispatch(vehicleActions.createManualVehicleAccount(manualVehicleSubmittingObject));
          tracker.track(tracker.events.kbbAddManual);
          return;
        }
        if (payload.dialogStep === 'review') {
          const kbbVehicleSubmittingObject: KbbVehicleSubmittingObject = {
            name: payload.name,
            year: payload.year,
            make: payload.make?.makeName ?? '',
            model: payload.model?.modelName ?? '',
            trim: payload.trim?.trimName ?? '',
            condition: formatConditionForBackend(payload.condition ?? ''),
            mileage: payload.miles,
            zipCode: payload.zipCode,
            vehicleId: payload.vehicleId ?? 0,
            scope: scope,
            linkTo: payload.linkTo,
            businessId: payload.businessId,
          };
          reduxDispatch(vehicleActions.createKbbVehicleAccount(kbbVehicleSubmittingObject));
          if (payload.linkTo === '0') {
            tracker.track(tracker.events.kbbAddKbbVehicle, { userAction: 'newVehicle' });
          } else {
            tracker.track(tracker.events.kbbAddKbbVehicle, { userAction: 'linkedVehicle' });
          }
          return;
        }
      }}
    >
      <VehicleDialogInner derivedLinkTo={derivedLinkTo} />
    </FormProvider>
  );
};

export default VehicleDialog;
